export const FULL_NAME_FORMAT_HINT = 'Lastname, Firstname; Lastname, Firstname MI.; or Lastname, Firstname Middlename';
export const FULL_NAME_FORMAT_EXAMPLE = 'Dela Cruz, Juan S.';

export interface FullNameValidationResult {
  isValid: boolean;
  message: string;
  formattedValue: string;
}

function buildFormatMessage(): string {
  return `Use one of these formats: ${FULL_NAME_FORMAT_HINT}. Example: ${FULL_NAME_FORMAT_EXAMPLE}`;
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function formatNameWord(word: string): string {
  return word
    .split(/([-'`])/)
    .map((part) => {
      if (/^[-'`]$/.test(part)) {
        return part;
      }

      const lower = part.toLowerCase();
      return lower ? lower[0].toUpperCase() + lower.slice(1) : lower;
    })
    .join('');
}

function formatNameSegment(segment: string): string {
  return normalizeWhitespace(segment)
    .split(' ')
    .filter(Boolean)
    .map(formatNameWord)
    .join(' ');
}

export function normalizeStudentId(studentId: string): string {
  return String(studentId || '').trim().toUpperCase().replace(/\s+/g, '');
}

function formatInitials(segment: string): string {
  const letters = segment.replace(/[^A-Za-z]/g, '').toUpperCase();
  return letters ? `${letters}.` : '';
}

function formatMiddleSegment(segment: string): string {
  const normalizedSegment = normalizeWhitespace(segment);
  if (!normalizedSegment) {
    return '';
  }

  const tokens = normalizedSegment.split(' ').filter(Boolean);
  const initialsOnly = tokens.every((token) => /^[A-Za-z]{1,3}(?:\.)?$/.test(token));

  if (initialsOnly) {
    return formatInitials(tokens.join(''));
  }

  return tokens.map(formatNameWord).join(' ');
}

function parseStudentFullName(fullName: string): string {
  const normalizedInput = normalizeWhitespace(fullName || '');
  if (!normalizedInput) {
    return '';
  }

  const commaIndex = normalizedInput.indexOf(',');
  if (commaIndex <= 0) {
    return '';
  }

  const lastName = normalizedInput.slice(0, commaIndex).trim();
  const rightSide = normalizeWhitespace(normalizedInput.slice(commaIndex + 1));
  if (!lastName || !rightSide) {
    return '';
  }

  const rightSideNoTrailingDots = rightSide.replace(/\.+$/g, '');
  const tokens = rightSideNoTrailingDots.split(' ').filter(Boolean);
  if (tokens.length === 0) {
    return '';
  }

  const formattedLastName = formatNameSegment(lastName);
  if (tokens.length === 1) {
    return `${formattedLastName}, ${formatNameSegment(tokens[0])}`;
  }

  const middleToken = tokens[tokens.length - 1];
  const firstName = tokens.slice(0, -1).join(' ');
  const formattedFirstName = formatNameSegment(firstName);
  const formattedMiddle = formatMiddleSegment(middleToken);

  if (!formattedFirstName) {
    return '';
  }

  if (!formattedMiddle) {
    return `${formattedLastName}, ${formattedFirstName}`;
  }

  return `${formattedLastName}, ${formattedFirstName} ${formattedMiddle}`;
}

export function validateStudentFullNameFormat(fullName: string): FullNameValidationResult {
  const formattedValue = parseStudentFullName(fullName);
  if (!normalizeWhitespace(fullName || '')) {
    return {
      isValid: false,
      message: buildFormatMessage(),
      formattedValue: '',
    };
  }

  if (!formattedValue) {
    return {
      isValid: false,
      message: buildFormatMessage(),
      formattedValue: normalizeWhitespace(fullName || ''),
    };
  }

  return {
    isValid: true,
    message: 'Full name format is valid.',
    formattedValue,
  };
}

export function normalizeImportedStudentFullName(fullName: string): FullNameValidationResult {
  return validateStudentFullNameFormat(fullName);
}
